import React from "react";
import ToolFileUploadField from "./ToolFileUploadField";

/**
 * Shared product-source picker for every Amazon Manager tool. Shopify is the
 * source of truth, so all tools select which products to act on the same way:
 *   - file   → upload a file of parent SKUs
 *   - vendor → a Shopify vendor name (all its products)
 *   - sku    → a single parent SKU
 *
 * Controlled component. The parent owns the state and passes setters. Use
 * `buildAmazonSourceFormData` (below) in the submit handler to turn this into
 * the multipart body the backend expects.
 */
export const AMAZON_MODES = { FILE: "file", VENDOR: "vendor", SKU: "sku" };

export function buildAmazonSourceFormData({ mode, file, vendor, parentSku }) {
  const formData = new FormData();
  formData.append("mode", mode);
  if (mode === AMAZON_MODES.FILE) {
    if (!file) throw new Error("Please upload a file of parent SKUs.");
    formData.append("input_file", file);
  } else if (mode === AMAZON_MODES.VENDOR) {
    const v = (vendor || "").trim();
    if (!v) throw new Error("Please enter a vendor name.");
    formData.append("vendor", v);
  } else {
    const s = (parentSku || "").trim();
    if (!s) throw new Error("Please enter a parent SKU.");
    formData.append("parent_sku", s);
  }
  return formData;
}

function AmazonSourceSelector({
  mode,
  setMode,
  file,
  setFile,
  vendor,
  setVendor,
  parentSku,
  setParentSku,
  disabled = false,
}) {
  return (
    <section className="tool-section">
      <div className="tool-section-header">
        <h3 className="tool-section-title">Products to check</h3>
      </div>
      <div className="tool-field-stack">
        <p className="tool-helper-text">
          Products and reference prices come from Shopify (your MAP-compliant source of
          truth), then are matched to Amazon by SKU. Pick how to choose them:
        </p>
        <div className="tool-radio-group" role="radiogroup" aria-label="Product source">
          <label className="tool-radio-option">
            <input
              type="radio"
              name="amazon-source-mode"
              value={AMAZON_MODES.VENDOR}
              checked={mode === AMAZON_MODES.VENDOR}
              onChange={() => setMode(AMAZON_MODES.VENDOR)}
              disabled={disabled}
            />
            <span>By vendor (all products)</span>
          </label>
          <label className="tool-radio-option">
            <input
              type="radio"
              name="amazon-source-mode"
              value={AMAZON_MODES.FILE}
              checked={mode === AMAZON_MODES.FILE}
              onChange={() => setMode(AMAZON_MODES.FILE)}
              disabled={disabled}
            />
            <span>Upload parent-SKU file</span>
          </label>
          <label className="tool-radio-option">
            <input
              type="radio"
              name="amazon-source-mode"
              value={AMAZON_MODES.SKU}
              checked={mode === AMAZON_MODES.SKU}
              onChange={() => setMode(AMAZON_MODES.SKU)}
              disabled={disabled}
            />
            <span>Single parent SKU</span>
          </label>
        </div>

        {mode === AMAZON_MODES.VENDOR ? (
          <>
            <label className="tool-label" htmlFor="amazon-vendor">
              Shopify vendor name
            </label>
            <input
              id="amazon-vendor"
              type="text"
              className="tool-input"
              placeholder="e.g. Vionic"
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              disabled={disabled}
            />
            <span className="tool-helper-text">
              All active products for this Shopify vendor are checked against Amazon.
            </span>
          </>
        ) : null}

        {mode === AMAZON_MODES.SKU ? (
          <>
            <label className="tool-label" htmlFor="amazon-parent-sku">
              Parent SKU
            </label>
            <input
              id="amazon-parent-sku"
              type="text"
              className="tool-input"
              placeholder="e.g. 2005149-003"
              value={parentSku}
              onChange={(e) => setParentSku(e.target.value)}
              disabled={disabled}
            />
            <span className="tool-helper-text">
              Every Shopify variant under this parent SKU is checked against Amazon.
            </span>
          </>
        ) : null}

        {mode === AMAZON_MODES.FILE ? (
          <ToolFileUploadField
            label="Parent-SKU file"
            file={file}
            setFile={setFile}
            accept=".xlsx,.xls,.csv"
            helperText="CSV/XLSX with a Parent SKU column (accepts 'Parent SKU', 'SKU', 'Style')."
          />
        ) : null}
      </div>
    </section>
  );
}

export default AmazonSourceSelector;
